import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import { STAFF_VIEW_WORDING } from "@shared-core/domain/supplier-portal-wording";

/**
 * True inside the staff view of the supplier portal (docs/features/supplier-portal-view-as/05 §6.5): staff look at
 * a supplier's pages as the supplier sees them, and every control that would change something is drawn switched
 * off. Only the staff-view build's layout provides it; the supplier build never does, so it reads false there and
 * every use of it is a no-op.
 */
export const StaffViewContext = createContext(false);

export function useIsStaffView(): boolean {
  return useContext(StaffViewContext);
}

/** The page's one read-only note, which every switched-off control on the page points at (`aria-describedby`). */
export const STAFF_VIEW_READ_ONLY_NOTE_ID = "staff-view-read-only-note";

// The read-only sentence is said once per page (Ben's wording rule, "one thing once"): switched-off controls
// register while they are on the page, and the page's one `StaffViewReadOnlyNoteOnce` shows while any are.
const RegisterControlContext = createContext<(() => () => void) | null>(null);
const SwitchedOffControlsContext = createContext(0);
const StaffViewSupplierNameContext = createContext<string | null>(null);

/**
 * The staff view: tells the pages they are one, counts the switched-off controls on the current page, and carries
 * the viewed supplier's name for the pages that name it (null until the staff session is read).
 */
export function StaffViewProvider({
  supplierName = null,
  children,
}: {
  supplierName?: string | null;
  children: ReactNode;
}) {
  const [count, setCount] = useState(0);
  const register = useCallback(() => {
    setCount((n) => n + 1);
    return () => setCount((n) => n - 1);
  }, []);
  return (
    <StaffViewContext.Provider value={true}>
      <RegisterControlContext.Provider value={register}>
        <SwitchedOffControlsContext.Provider value={count}>
          <StaffViewSupplierNameContext.Provider value={supplierName}>
            {children}
          </StaffViewSupplierNameContext.Provider>
        </SwitchedOffControlsContext.Provider>
      </RegisterControlContext.Provider>
    </StaffViewContext.Provider>
  );
}

/** Counts the calling component as a switched-off control while it is mounted in a staff view. */
export function useRegisterSwitchedOffControl(): void {
  const isStaffView = useIsStaffView();
  const register = useContext(RegisterControlContext);
  useEffect(
    () => (isStaffView && register ? register() : undefined),
    [isStaffView, register],
  );
}

export function useSwitchedOffControlCount(): number {
  return useContext(SwitchedOffControlsContext);
}

/** The name of the supplier being viewed, or null outside the staff view or before its session is read. */
export function useStaffViewSupplierName(): string | null {
  return useContext(StaffViewSupplierNameContext);
}

/**
 * The sentence under a not-found title: the supplier's own `supplierWhy` (written for someone who came from an
 * email), or in the staff view what is true for staff.
 */
export function useNotFoundWhy(
  what: "order" | "pick" | "quote request",
  supplierWhy: string,
): string {
  const isStaffView = useIsStaffView();
  const supplierName = useStaffViewSupplierName();
  return isStaffView
    ? STAFF_VIEW_WORDING.notFound(what, supplierName)
    : supplierWhy;
}
